/**
 * ListingCarousel Component
 *
 * Horizontal carousel of listing cards.
 * Accepts pre-normalized ListingCardData[] — normalization happens
 * in the parent page (composition root), not here.
 *
 * Server Component wrapper - carousel interactivity handled by client.
 */
import { ListingCard } from '@/shared/components/listings';
import { getListingHref } from '@/shared/lib/listings/url';
import type { ListingCardData } from '@/shared/lib/types/listing-card';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import type { Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';
import { CarouselItem } from '@letsgethome/ui/carousel';

import { ListingCarouselClient } from './ListingCarouselClient';

interface ListingCarouselProps {
  /** Section title — must come from `t()` or `plainText()` (no raw literal). */
  title: LocalizedText;
  /** Optional href - title becomes clickable link with chevron */
  seeAllHref?: Route;
  /** Pre-normalized listing data (normalized in page/action, not here) */
  listings: ListingCardData[];
  /** Which price to show: "rent" or "sale" */
  transactionType?: TransactionModeValue;
}

export function ListingCarousel({
  title,
  seeAllHref,
  listings,
  transactionType = 'rent',
}: ListingCarouselProps) {
  if (listings.length === 0) {
    return null;
  }

  return (
    <section className="w-full">
      <ListingCarouselClient title={title} seeAllHref={seeAllHref}>
        {listings.map((listing) => (
          <CarouselItem
            key={listing.id}
            className="pl-4 basis-[280px] sm:basis-[300px] lg:basis-[320px]"
          >
            <ListingCard
              listing={listing}
              transactionType={transactionType}
              variant="compact"
              href={getListingHref(listing)}
              sizes="320px"
            />
          </CarouselItem>
        ))}
      </ListingCarouselClient>
    </section>
  );
}
